import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  deleteDoc 
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const invitationService = {
  // Send invitation to join family
  async sendInvitation(familyId, invitedBy, invitedEmail) {
    try {
      // Check if invitation already exists for this email and family
      const invitationsRef = collection(db, 'invitations');
      const existingQuery = query(
        invitationsRef, 
        where('familyId', '==', familyId),
        where('invitedEmail', '==', invitedEmail),
        where('status', '==', 'pending')
      );
      
      const existingInvitations = await getDocs(existingQuery);
      if (!existingInvitations.empty) {
        return { success: false, error: 'Invitation already sent to this email' };
      }

      // Generate invitation token
      const token = this.generateInvitationToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      const invitationRef = doc(invitationsRef);
      const invitationData = {
        id: invitationRef.id,
        familyId,
        invitedBy,
        invitedEmail,
        status: 'pending',
        token,
        expiresAt,
        createdAt: new Date(),
        acceptedAt: null
      };

      await setDoc(invitationRef, invitationData);

      return { 
        success: true, 
        invitationId: invitationRef.id, 
        token,
        invitation: invitationData 
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Accept invitation
  async acceptInvitation(token, userId) {
    try {
      // Find invitation by token
      const invitationsRef = collection(db, 'invitations');
      const tokenQuery = query(invitationsRef, where('token', '==', token));
      const invitationDocs = await getDocs(tokenQuery);

      if (invitationDocs.empty) {
        return { success: false, error: 'Invalid invitation token' };
      }

      const invitationDoc = invitationDocs.docs[0];
      const invitationData = invitationDoc.data();

      // Check if invitation is still valid
      if (invitationData.status !== 'pending') {
        return { success: false, error: 'Invitation is no longer valid' };
      }

      if (new Date() > invitationData.expiresAt.toDate()) {
        // Mark as expired
        await updateDoc(invitationDoc.ref, {
          status: 'expired',
          updatedAt: new Date()
        });
        return { success: false, error: 'Invitation has expired' };
      }

      // Update invitation status
      await updateDoc(invitationDoc.ref, {
        status: 'accepted',
        acceptedAt: new Date(),
        acceptedBy: userId
      });

      return { 
        success: true, 
        familyId: invitationData.familyId,
        invitation: invitationData 
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Reject invitation
  async rejectInvitation(token) {
    try {
      // Find invitation by token
      const invitationsRef = collection(db, 'invitations');
      const tokenQuery = query(invitationsRef, where('token', '==', token));
      const invitationDocs = await getDocs(tokenQuery);

      if (invitationDocs.empty) {
        return { success: false, error: 'Invalid invitation token' };
      }

      const invitationDoc = invitationDocs.docs[0];
      const invitationData = invitationDoc.data();

      // Update invitation status
      await updateDoc(invitationDoc.ref, {
        status: 'rejected',
        rejectedAt: new Date()
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get invitation by token
  async getInvitationByToken(token) {
    try {
      const invitationsRef = collection(db, 'invitations');
      const tokenQuery = query(invitationsRef, where('token', '==', token));
      const invitationDocs = await getDocs(tokenQuery);

      if (invitationDocs.empty) {
        return { success: false, error: 'Invitation not found' };
      }

      const invitationData = invitationDocs.docs[0].data();
      
      // Check if expired
      if (new Date() > invitationData.expiresAt.toDate() && invitationData.status === 'pending') {
        // Mark as expired
        await updateDoc(invitationDocs.docs[0].ref, {
          status: 'expired'
        });
        return { success: false, error: 'Invitation has expired' };
      }

      return { success: true, invitation: invitationData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get invitations for a family
  async getFamilyInvitations(familyId) {
    try {
      const invitationsRef = collection(db, 'invitations');
      const familyQuery = query(invitationsRef, where('familyId', '==', familyId));
      const invitationDocs = await getDocs(familyQuery);

      const invitations = invitationDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, invitations };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Cancel invitation
  async cancelInvitation(invitationId) {
    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);

      if (!invitationDoc.exists()) {
        return { success: false, error: 'Invitation not found' };
      }

      const invitationData = invitationDoc.data();
      
      if (invitationData.status !== 'pending') {
        return { success: false, error: 'Can only cancel pending invitations' };
      }

      await updateDoc(invitationRef, {
        status: 'cancelled',
        cancelledAt: new Date()
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete expired invitations (cleanup)
  async cleanupExpiredInvitations() {
    try {
      const invitationsRef = collection(db, 'invitations');
      const expiredQuery = query(
        invitationsRef, 
        where('expiresAt', '<', new Date()),
        where('status', '==', 'pending')
      );
      
      const expiredDocs = await getDocs(expiredQuery);
      
      const deletePromises = expiredDocs.docs.map(doc => 
        updateDoc(doc.ref, { status: 'expired' })
      );

      await Promise.all(deletePromises);

      return { success: true, count: expiredDocs.docs.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Generate a random invitation token
  generateInvitationToken() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 32; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  },

  // Create invitation link
  createInvitationLink(token) {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite/${token}`;
  }
};

